
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ProductInterestCTA from '../../components/feature/ProductInterestCTA';
import { featuredProducts } from '../../mocks/products';

// Helper function to get series key for translations
const getSeriesKey = (series: string): string => {
  const seriesMap: Record<string, string> = {
    'Royal Diamond': 'royalDiamond',
    'Decori': 'decori',
    'Modern Steel': 'modernSteel',
    'Fortuna': 'fortuna',
    'Fortuna Supreme': 'fortunaSupreme',
    'Mazar': 'mazar',
    'Mazar 3D': 'mazar3D',
    'Deep Diamond': 'deepDiamond',
    'Verdi Fires': 'verdiFires'
  };
  return seriesMap[series] || 'royalDiamond';
};

// Helper function to get feature translation key
const getFeatureKey = (feature: string): string => {
  if (feature.includes('ARS') || feature.includes('Anti Reflective')) return 'ars';
  if (feature.includes('aluminium omlijsting') || feature.includes('aluminum frame')) return 'blackAluminumFrame';
  if (feature.includes('glazen omlijsting') || feature.includes('glass frame')) return 'blackGlassFrame';
  if (feature.includes('SLT 2.0')) return 'slt20';
  if (feature.includes('SLT') || feature.includes('SMART LED')) return 'slt';
  if (feature.includes('Bodemverlichting in 10') || feature.includes('bottom lighting in 10')) return 'bottomLighting10';
  if (feature.includes('Bodemverlichting instelbaar') || feature.includes('bottom lighting adjustable')) return 'bottomLighting';
  if (feature.includes('Vlamhelderheid instelbaar') || feature.includes('flame brightness adjustable')) return 'flameBrightness5';
  if (feature.includes('Vlamhelderheid:') || feature.includes('flame brightness:')) return 'flameBrightness';
  if (feature.includes('Vlamsnelheid instelbaar in 5') || feature.includes('flame speed adjustable in 5')) return 'flameSpeed5';
  if (feature.includes('Vlamsnelheid instelbaar in 4') || feature.includes('flame speed adjustable in 4')) return 'flameSpeed4';
  if (feature.includes('Vlamsnelheid:') || feature.includes('flame speed:')) return 'flameSpeed';
  if (feature.includes('Touchscreen') || feature.includes('afstandsbediening')) return 'touchscreenRemote';
  if (feature.includes('luxe houtset') || feature.includes('luxury wood set')) return 'woodsetCrystals';
  if (feature.includes('Deluxe houtset') || feature.includes('Deluxe wood set')) return 'deluxeWoodset';
  if (feature.includes('Verwarmingsvermogen') || feature.includes('heating power')) return 'heatingPower';
  if (feature.includes('thermostaat') || feature.includes('thermostat')) return 'digitalThermostat';
  if (feature.includes('Oververhittingsbeveiliging') || feature.includes('overheat')) return 'overheatProtection';
  if (feature.includes('garantie') || feature.includes('warranty')) return 'warranty2year';
  if (feature.includes('6 kleuren') || feature.includes('6 colors')) return 'mainFlame6Colors';
  if (feature.includes('3 kleuren') || feature.includes('3 colors')) return 'mainFlame3Colors';
  if (feature.includes('inbouwdiepte') || feature.includes('installation depth')) return 'minDepth14cm';
  if (feature.includes('afvoerkanalen') || feature.includes('exhaust ducts')) return 'noVentilation';
  if (feature.includes('met en zonder verwarming') || feature.includes('with and without heating')) return 'worksWithWithoutHeating';
  if (feature.includes('Muurbeugel') || feature.includes('wall bracket')) return 'wallBracketIncluded';
  if (feature.includes('Stalen matzwarte') || feature.includes('steel matte black')) return 'steelMatteBlackFrame';
  if (feature.includes('3 kleurvlammen') || feature.includes('3 flame colors')) return 'threeFlameColors';
  if (feature.includes('LED-bodemverlichting') || feature.includes('LED bottom')) return 'ledBottomLighting';
  if (feature.includes('Verwarming') && !feature.includes('vermogen')) return 'heating';
  if (feature.includes('Extra diepe haard')) return 'extraDeepMirrored';
  if (feature.includes('Op 3 manieren in te bouwen')) return 'threeInstallationModes';
  if (feature.includes('Hoofdvlam instelbaar in 6')) return 'mainFlame6Colors';
  if (feature.includes('Hoofdvlam instelbaar in 3')) return 'mainFlame3Colors';
  if (feature.includes('Realistisch en sfeervol')) return 'realisticFlame';
  if (feature.includes('Vrijstaand')) return 'freestanding';
  if (feature.includes('3-zijdig vuurzicht')) return 'threeSidedView';
  if (feature.includes('Twee verwarmingsstanden')) return 'twoHeatingModes';
  if (feature.includes('Geschikt voor ruimtes')) return 'suitableFor20to25m2';
  if (feature.includes('Ideaal als bijverwarming')) return 'idealSupplementaryHeating';
  if (feature.includes('veiligheidsuitschakeling')) return 'safetyShutoff';
  if (feature.includes('Onderhoudsvrij')) return 'maintenanceFree';
  if (feature.includes('Rustieke afwerking')) return 'rusticFinish';
  if (feature.includes('Handbediening')) return 'manualControl';
  if (feature.includes('Afvoerloos')) return 'noVentilation';
  return '';
};

// Helper to translate flame colors
const getFlameColorKey = (color: string): string => {
  const colorMap: Record<string, string> = {
    'Oranje': 'orange',
    'Orange': 'orange',
    'Blauw': 'blue',
    'Blue': 'blue',
    'Goud': 'gold',
    'Gold': 'gold',
    'Oranje/Blauw': 'orangeBlue',
    'Orange/Blue': 'orangeBlue',
    'Geel/Goud': 'yellowGold',
    'Yellow/Gold': 'yellowGold',
    'Multi-colour': 'multiColor',
    'Multi-color': 'multiColor'
  };
  return colorMap[color] || 'orange';
};

// Helper to translate category
const getCategoryKey = (category: string): string => {
  const categoryMap: Record<string, string> = {
    'Inbouwhaarden': 'builtIn',
    'Built-in Fireplaces': 'builtIn',
    '3-zijdige Haarden': 'threeSided',
    '3-Sided Fireplaces': 'threeSided',
    'Vrijstaande Haarden': 'freestandingFireplaces',
    'Freestanding Fireplaces': 'freestandingFireplaces'
  };
  return categoryMap[category] || 'builtIn';
};

export default function ProductPage() {
  const { id } = useParams();
  const { t } = useTranslation(['products', 'common']);
  const [selectedImage, setSelectedImage] = useState(0);

  const product = featuredProducts.find(p => p.id === Number(id));

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('products:common.productNotFound')}</h1>
          <Link to="/" className="text-amber-600 hover:text-amber-700 cursor-pointer">
            {t('products:common.backToHome')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = product.images || [product.image];
  const seriesKey = getSeriesKey(product.series);
  const categoryKey = getCategoryKey(product.category);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-amber-600 cursor-pointer">{t('common:nav.home')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-4">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-lg overflow-hidden cursor-pointer transition-all ${
                        selectedImage === index 
                          ? 'ring-2 ring-amber-500' 
                          : 'ring-1 ring-gray-200 hover:ring-gray-300'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="inline-block px-3 py-1 bg-amber-100 rounded-full mb-4">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                  {product.series}
                </span>
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                {product.name}
              </h1>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t(`products:series.${seriesKey}.description`)}
              </p>

              {/* Specifications */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('products:common.technicalSpecs')}</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">{t('products:common.dimensions')}</span>
                    <span className="text-sm font-semibold text-gray-900">{product.dimensions}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">{t('products:common.power')}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {product.power === 'Geen verwarming' ? t('products:common.noHeating') : product.power}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">{t('products:common.category')}</span>
                    <span className="text-sm font-semibold text-gray-900">{t(`products:categories.${categoryKey}`)}</span>
                  </div>
                </div>
              </div>

              {/* Flame Colors */}
              {product.flameColors && product.flameColors.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('products:common.flameColors')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.flameColors.map((color, index) => (
                      <span key={index} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                        {t(`products:flameColors.${getFlameColorKey(color)}`)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('products:common.features')}</h3>
                <div className="grid grid-cols-1 gap-3">
                  {product.features.map((feature, index) => {
                    const featureKey = getFeatureKey(feature);
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-fill text-amber-600 text-sm"></i>
                        </div>
                        <span className="text-sm text-gray-700">
                          {featureKey ? t(`products:features.${featureKey}`) : feature}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* B2B CTA */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500 flex items-center justify-center">
                  <i className="ri-lock-line text-2xl text-black"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{t('products:common.dealerOnly')}</h3>
                <p className="text-sm text-gray-300 mb-6">
                  {t('products:common.dealerOnlyDescription')}
                </p>
                <Link
                  to="/dealer-registration"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
                >
                  {t('products:common.becomeDealer')}
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('products:common.relatedProducts', { series: product.series })}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts
              .filter(p => p.series === product.series && p.id !== product.id)
              .slice(0, 4)
              .map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group cursor-pointer"
                >
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-gray-100 mb-4">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">{relatedProduct.name}</h3>
                  <p className="text-sm text-gray-600">{relatedProduct.specs}</p>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <ProductInterestCTA variant="full" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
